import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { todayLong } from "@/lib/format";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { useEnabled, usePages, useTexts } from "@/lib/layout";

const navCls = ({ isActive }) =>
  `relative shrink-0 px-3 py-3 text-[12.5px] font-semibold uppercase tracking-[0.09em] transition-colors hover:text-primary ${
    isActive ? "text-primary after:absolute after:inset-x-3 after:bottom-0 after:h-[2px] after:bg-primary" : ""
  }`;

const Masthead = ({ settings, t }) => {
  const [first, ...rest] = (settings?.site_name || "El Noticiero").split(" ");
  return (
    <div className="wrap grid md:grid-cols-12 items-end gap-4 py-7 md:py-10">
      <Link to="/" className="md:col-span-8 group" data-testid="masthead-logo-link" aria-label="Ir a la portada">
        {rest.length > 0 && <span className="block font-display italic text-primary text-xl md:text-3xl ml-1 -mb-1 md:-mb-3">{first}</span>}
        <span className="block font-display font-black tracking-[-0.03em] leading-[0.88] text-[3.4rem] sm:text-7xl lg:text-[7rem]">
          {rest.length ? rest.join(" ") : first}
        </span>
      </Link>
      <div className="md:col-span-4 md:text-right">
        <p className="font-display italic text-lg md:text-xl text-muted-foreground leading-snug">{settings?.tagline}</p>
        {t.masthead_kicker && <p className="kicker mt-3 text-foreground/70">{t.masthead_kicker}</p>}
      </div>
    </div>
  );
};

export const Header = ({ categories, settings }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const t = useTexts("layout");
  const ts = useTexts("search");
  const searchOn = useEnabled("search");
  const topbar = usePages().filter((p) => p.in_topbar);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 220);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/buscar?q=${encodeURIComponent(q.trim())}`);
    setOpen(false);
  };

  return (
    <>
      <div className="border-b border-border">
        <div className="wrap flex items-center justify-between py-2 kicker text-[10px] text-muted-foreground">
          <span className="first-letter:uppercase" data-testid="header-date">{todayLong()}</span>
          <span className="hidden md:inline" data-testid="header-topbar-center">{t.topbar_center}</span>
          <div className="flex items-center gap-5">
            {topbar.map((p) => (
              <Link key={p.slug} to={`/${p.slug}`} className="hidden sm:inline hover:text-primary" data-testid={`header-page-link-${p.slug}`}>{p.nav_label || p.title}</Link>
            ))}
            <ThemeToggle />
          </div>
        </div>
      </div>
      <Masthead settings={settings} t={t} />
      <nav className="sticky top-0 z-50 border-y-2 border-foreground bg-background/85 backdrop-blur-xl" aria-label="Secciones">
        <div className="wrap flex items-center h-12">
          <Link
            to="/"
            className={`font-display font-black text-xl tracking-tight transition-all duration-300 overflow-hidden whitespace-nowrap ${scrolled ? "max-w-[140px] opacity-100 mr-4" : "max-w-0 opacity-0"}`}
            data-testid="nav-compact-logo"
          >
            El Noticiero
          </Link>
          <div className="flex-1 flex overflow-x-auto no-scrollbar -ml-3">
            <NavLink to="/" end className={navCls} data-testid="nav-link-portada">{t.nav_home}</NavLink>
            {categories.map((c) => (
              <NavLink key={c.slug} to={`/seccion/${c.slug}`} className={navCls} data-testid={`nav-link-${c.slug}`}>
                {c.name}
              </NavLink>
            ))}
          </div>
          {searchOn && (
            <button onClick={() => setOpen((o) => !o)} className="ml-3 p-2 hover:text-primary transition-colors" aria-label={ts.button} data-testid="nav-search-toggle">
              {open ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
            </button>
          )}
        </div>
        {open && searchOn && (
          <form onSubmit={submit} className="border-t border-border bg-background" data-testid="nav-search-form">
            <div className="wrap flex items-center gap-3 py-3">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={ts.menu_placeholder}
                className="flex-1 bg-transparent font-display text-xl outline-none placeholder:text-muted-foreground/60"
                data-testid="nav-search-input"
              />
              <button type="submit" className="kicker text-primary" data-testid="nav-search-submit">{ts.button}</button>
            </div>
          </form>
        )}
      </nav>
    </>
  );
};
